import { type ReactiveController, type ReactiveControllerHost } from "lit";

export const supportedThemes = ["light", "dark", "system"] as const;
export type Theme = (typeof supportedThemes)[number];
export type ResolvedTheme = "light" | "dark";

const storageKey = "theme";

const themeChangeListeners = new Set<(theme: Theme, resolvedTheme: ResolvedTheme) => void>();

function getSystemTheme(): ResolvedTheme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === "system" ? getSystemTheme() : theme;
}

export function getTheme(): Theme {
  try {
    const savedTheme = localStorage.getItem(storageKey) as Theme;
    return supportedThemes.includes(savedTheme) ? savedTheme : "system";
  } catch {
    return "system";
  }
}

export function getResolvedTheme(): ResolvedTheme {
  return resolveTheme(getTheme());
}

function applyTheme(theme: Theme) {
  const resolvedTheme = resolveTheme(theme);
  document.documentElement.setAttribute("data-theme", resolvedTheme);

  // Notify all listeners
  for (const listener of themeChangeListeners) {
    listener(theme, resolvedTheme);
  }
}

export function setTheme(theme: Theme) {
  try {
    localStorage.setItem(storageKey, theme);
  } catch {
    // Silently fail if localStorage is not available
  }

  applyTheme(theme);
}

export class ThemeController implements ReactiveController {
  private unsubscribe?: () => void;

  constructor(private host: ReactiveControllerHost) {
    this.host.addController(this);
  }

  get theme(): Theme {
    return getTheme();
  }

  get resolvedTheme(): ResolvedTheme {
    return getResolvedTheme();
  }

  setTheme(theme: Theme) {
    setTheme(theme);
  }

  hostConnected() {
    this.unsubscribe = this.onThemeChange(() => {
      this.host.requestUpdate();
    });
  }

  hostDisconnected() {
    this.unsubscribe?.();
  }

  private onThemeChange(callback: (theme: Theme, resolvedTheme: ResolvedTheme) => void): () => void {
    themeChangeListeners.add(callback);
    return () => themeChangeListeners.delete(callback);
  }
}

// Auto-initialize theme on module load
(function initializeTheme() {
  const savedTheme = getTheme();
  applyTheme(savedTheme);

  // Listen for system theme changes when using system theme
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const handleSystemThemeChange = () => {
    const currentTheme = getTheme();
    if (currentTheme === "system") {
      applyTheme(currentTheme);
    }
  };

  // Modern browsers
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener("change", handleSystemThemeChange);
  } else {
    // Legacy browsers
    mediaQuery.addListener(handleSystemThemeChange);
  }
})();
